'use client';

import { useCallback, useEffect, useEffectEvent, useState } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { analytics } from '@kit/analytics';
import { isInternalEmail } from '@kit/shared/analytics';
import {
  AppEvent,
  AppEventType,
  ConsumerProvidedEventTypes,
  useAppEvents,
} from '@kit/shared/events';
import { isBrowser } from '@kit/shared/utils';

import { createGtagStub } from '~/lib/gtag-stub';

/**
 * #592: Merkt, ob die aktuelle (eingeloggte) Session zu einer internen Test-/
 * Mitarbeiter-Identität gehört. Gesetzt aus dem `user.signedIn`-Event (trägt die
 * Email), feuert bei jedem `INITIAL_SESSION` (= jeder Reload) → greift auch für
 * bereits eingeloggte interne User. Gated den Meta-Pixel (PageView/SignupStart).
 * Modul-Singleton im Client; wird bei Reload neu evaluiert.
 */
let isInternalSession = false;

type AnalyticsMapping<
  T extends ConsumerProvidedEventTypes = NonNullable<unknown>,
> = {
  [K in AppEventType<T>]?: (event: AppEvent<T, K>) => unknown;
};

/**
 * Hook to subscribe to app events and map them to analytics actions
 * @param mapping
 */
function useAnalyticsMapping<T extends ConsumerProvidedEventTypes>(
  mapping: AnalyticsMapping<T>,
) {
  const appEvents = useAppEvents<T>();

  const subscribeToAppEvent = useEffectEvent(
    (
      eventType: AppEventType<T>,
      handler: (event: AppEvent<T, AppEventType<T>>) => unknown,
    ) => {
      appEvents.on(eventType, handler);
    },
  );

  const unsubscribeFromAppEvent = useEffectEvent(
    (
      eventType: AppEventType<T>,
      handler: (event: AppEvent<T, AppEventType<T>>) => unknown,
    ) => {
      appEvents.off(eventType, handler);
    },
  );

  useEffect(() => {
    const subscriptions = Object.entries(mapping).map(
      ([eventType, handler]) => {
        subscribeToAppEvent(eventType as AppEventType<T>, handler);

        return () =>
          unsubscribeFromAppEvent(eventType as AppEventType<T>, handler);
      },
    );

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [mapping]);
}

/**
 * Stringifiziert die datensparsamen Selbstauskunft-Config-Metadaten (§6.4)
 * für das string-typisierte trackEvent-Property-Interface.
 */
function selbstauskunftConfigProperties(payload: {
  preset: string;
  person_count: number;
  language: string;
  field_count: number;
}): Record<string, string> {
  return {
    preset: payload.preset,
    person_count: String(payload.person_count),
    language: payload.language,
    field_count: String(payload.field_count),
  };
}

/**
 * Define a mapping of app events to analytics actions
 * Add new mappings here to track new events in the analytics service from app events
 */
const analyticsMapping: AnalyticsMapping = {
  'user.signedIn': (event) => {
    const { userId, ...traits } = event.payload;

    if (userId) {
      // #592: interne Session merken (gated den Meta-Pixel) und als Person-
      // Property `is_internal` taggen (PostHog-Cohort/Filter). Die Email ist
      // bereits Teil von `traits` (→ Person-Property `email`, speist den nativen
      // PostHog-"internal users"-Filter).
      isInternalSession = isInternalEmail(
        (event.payload as { email?: string }).email,
      );

      return analytics.identify(userId, {
        ...traits,
        is_internal: String(isInternalSession),
      });
    }
  },
  'user.signedUp': (event) => {
    // SignupStart (#561): feuert beim FORM-SUBMIT, nicht beim Seitenbesuch.
    // window.fbq existiert nur, wenn der Pixel nach advertisement-Consent
    // gebootstrapped wurde → die Prüfung auf window.fbq ist zugleich das
    // Consent-Gate. CompleteRegistration feuert NICHT hier (das ist erst die
    // Email-Bestätigung, serverseitig via CAPI im auth/confirm-Handler).
    // #592: für interne Test-/Mitarbeiter-Identitäten NICHT an Meta senden.
    const internal = isInternalSession || isInternalEmail(event.payload.email);

    if (!internal && typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', 'SignupStart');
    }

    // Google-Ads-Signup-Conversion: analog zum Meta-Pixel feuert das Event
    // beim erfolgreichen Signup-Callback (user.signedUp = NACH Konto-Anlage,
    // nicht beim Formular-Öffnen).
    //
    // Die Prüfung auf window.gtag ist Consent-Gate UND Prod-Gate — ABER nur,
    // weil bootstrapGtag() in useGoogleAdsTag hinter hasAdvertisementConsent()
    // liegt (#717 Änderung 2). Zwischen der Umstellung auf Consent Mode v2
    // Advanced und #717 lud der Tag unconditional; die Aussage war in dieser
    // Zeit schlicht falsch. Wer das Gate dort entfernt, muss hier ein
    // explizites hasAdvertisementConsent() ergänzen.
    // Für interne Test-/Mitarbeiter-Identitäten NICHT senden (wie beim Pixel).
    const googleAdsId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
    const googleAdsSignupLabel =
      process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL;

    if (
      !internal &&
      typeof window !== 'undefined' &&
      window.gtag &&
      googleAdsId &&
      googleAdsSignupLabel
    ) {
      window.gtag('event', 'conversion', {
        send_to: `${googleAdsId}/${googleAdsSignupLabel}`,
      });
    }

    // Email nicht als Event-Property senden (ist bereits Person-Property via
    // identify) – nur `method` wie bisher.
    return analytics.trackEvent(event.type, { method: event.payload.method });
  },
  'checkout.started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'checkout.completed': (event) => {
    // Umsatz-Conversion (#421): PostHog-Name explizit snake_case.
    return analytics.trackEvent('checkout_completed', event.payload);
  },
  'user.updated': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  // Mieterselbstauskunft-Tool (Sanierungs-Spec §6.4): App-Event-Bus → PostHog,
  // snake_case Namen wie beim checkout_completed-Mapping. Payloads tragen NIE
  // Formular-Inhalte/Feld-Keys – nur Config-Metadaten (preset, person_count,
  // language, field_count; `logged_in` beim Save-Klick). Feuert automatisch
  // nur nach Analytics-Consent (CookieYes-Regime, unverändert).
  // trackEvent-Properties sind string-typisiert → Zahlen/Booleans stringifizieren.
  'selbstauskunft.toolOpened': () => {
    return analytics.trackEvent('selbstauskunft_tool_opened', {});
  },
  'selbstauskunft.generated': (event) => {
    return analytics.trackEvent(
      'selbstauskunft_generated',
      selbstauskunftConfigProperties(event.payload),
    );
  },
  'selbstauskunft.pdfDownloaded': (event) => {
    return analytics.trackEvent(
      'selbstauskunft_pdf_downloaded',
      selbstauskunftConfigProperties(event.payload),
    );
  },
  'selbstauskunft.workspaceSaveClicked': (event) => {
    return analytics.trackEvent('selbstauskunft_workspace_save_clicked', {
      ...selbstauskunftConfigProperties(event.payload),
      logged_in: String(event.payload.logged_in),
    });
  },
};

/**
 * CookieYes-Consent-State (window.getCkyConsent() return type).
 * Verfügbar nach `cookieyes_banner_load`-Event.
 */
interface CkyConsentState {
  activeLaw: string;
  categories: {
    necessary: boolean;
    functional: boolean;
    analytics: boolean;
    performance: boolean;
    advertisement: boolean;
  };
  isUserActionCompleted: boolean;
  consentID: string;
  languageCode: string;
}

declare global {
  interface Window {
    getCkyConsent?: () => CkyConsentState;
    fbq?: (...args: unknown[]) => void;
    // Google-Tag (gtag.js) – Google-Ads-Conversion-Tracking (consent-gated).
    // Existiert nur, wenn bootstrapGtag() nach advertisement-Consent lief.
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Synchronisiert analytics-Services mit CookieYes-Consent.
 *
 * Pattern (TTDSG § 25-konform):
 * 1. CookieYes-Banner laedt (cookieyes_banner_load event)
 * 2. Wenn analytics-Consent vorhanden → enableServices(['posthog'])
 * 3. Bei Consent-Update (cookieyes_consent_update event) → re-sync
 * 4. Bei Consent-Widerruf → disableServices() ruft posthog.shutdown() auf
 *
 * Wenn NEXT_PUBLIC_COOKIEYES_SITE_KEY nicht gesetzt ist (z.B. Staging):
 * Hook ist no-op, PostHog wird NIE initialisiert (sicherer Default).
 */
function useCookieYesConsentSync() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncConsentState = () => {
      const consent = window.getCkyConsent?.();
      if (!consent) return;

      if (consent.categories.analytics) {
        void analytics.enableServices(['posthog']);
      } else {
        void analytics.disableServices(['posthog']);
      }
    };

    // WICHTIG: CookieYes dispatcht seine Events auf `document`, NICHT auf window
    // (live verifiziert 2026-06-04 via dispatchEvent-Monitor). Vorher lauschten
    // wir auf window → die Listener feuerten NIE → enableServices(['posthog'])
    // wurde nie aufgerufen → PostHog initialisierte nie = Tracking-Blackout #431.
    document.addEventListener('cookieyes_banner_load', syncConsentState);
    document.addEventListener('cookieyes_consent_update', syncConsentState);
    // window-Fallback (defensiv, falls eine CookieYes-Version doch auf window feuert)
    window.addEventListener('cookieyes_consent_update', syncConsentState);

    // Returning-User-Fix (#431, reopened): CookieYes lädt via `afterInteractive`
    // und feuert `cookieyes_banner_load` teils BEVOR dieser Effect den Listener
    // anhängt. Bei Wiederkehrern mit bereits gesetztem Consent gibt es danach
    // KEIN `cookieyes_consent_update` mehr → PostHog wird nie aktiviert (live
    // bestätigt: erster Load bei vorhandenem Consent macht 0 /ingest-Requests).
    // Der einmalige sofortige Check verfehlt das, weil getCkyConsent zum Mount-
    // Zeitpunkt noch nicht existiert. Daher pollen wir, bis getCkyConsent da ist,
    // und syncen dann einmalig. enableServices ist idempotent; Live-Consent-
    // Änderungen laufen weiter über die Event-Listener.
    let synced = false;
    const trySyncOnce = () => {
      if (synced) return true;
      if (window.getCkyConsent?.()) {
        syncConsentState();
        synced = true;
        return true;
      }
      return false;
    };

    let polls = 0;
    const poll = window.setInterval(() => {
      polls += 1;
      // ~10s Timeout: ohne NEXT_PUBLIC_COOKIEYES_SITE_KEY (z.B. Staging) lädt
      // CookieYes nie → getCkyConsent bleibt undefined → Polling beenden.
      if (trySyncOnce() || polls > 50) {
        window.clearInterval(poll);
      }
    }, 200);

    // Sofort versuchen, falls CookieYes beim Mount bereits geladen war.
    trySyncOnce();

    return () => {
      window.clearInterval(poll);
      document.removeEventListener('cookieyes_banner_load', syncConsentState);
      document.removeEventListener(
        'cookieyes_consent_update',
        syncConsentState,
      );
      window.removeEventListener('cookieyes_consent_update', syncConsentState);
    };
  }, []);
}

/**
 * Liest den advertisement-Consent aus CookieYes. true nur, wenn CookieYes
 * geladen ist UND der User die Werbe-Kategorie aktiv akzeptiert hat.
 */
function hasAdvertisementConsent(): boolean {
  if (typeof window === 'undefined') return false;
  return window.getCkyConsent?.()?.categories.advertisement === true;
}

/**
 * Modul-Flag (Gürtel-und-Hosenträger gegen Race polling↔consent-event): stellt
 * sicher, dass das Meta-Pixel-Bootstrap-Snippet genau EINMAL injiziert wird,
 * selbst wenn Polling und CookieYes-Event quasi-gleichzeitig feuern. Das
 * offizielle Snippet hat zwar einen eigenen `if(f.fbq)return;`-Guard, dieser
 * greift aber erst NACH dem ersten Lauf.
 */
let pixelBootstrapped = false;

/** Interne Form des fbq-Stubs vor dem Laden von fbevents.js. */
interface FbqStub {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: FbqStub;
  loaded: boolean;
  version: string;
}

/**
 * Baut den fbq-Stub-Queue auf und injiziert fbevents.js async – typsichere
 * Nachbildung des offiziellen Meta-Pixel-Bootstrap-Snippets. Eigener Guard
 * gegen Doppel-Init.
 */
function bootstrapFbq(): void {
  if (window.fbq) return;

  const w = window as typeof window & { _fbq?: FbqStub };

  const fbq = function (this: unknown, ...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as FbqStub;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  window.fbq = fbq;
  if (!w._fbq) w._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';

  // In den <head> – s. bootstrapGtag(): das offizielle Meta-Snippet nutzt
  // `insertBefore(script, firstScript)`, was im <body> landet und waehrend der
  // Hydration React #418 ausloest. Gleiche Klasse wie #620 (posthog).
  document.head.appendChild(script);
}

/**
 * Meta-Pixel „light" (consent-gated, TTDSG § 25 / Art. 6 Abs. 1 lit. a DSGVO).
 *
 * - Lädt das offizielle Meta-Pixel-Bootstrap-Snippet NUR nach advertisement-
 *   Consent. Kein auto-PageView im Bootstrap (der route-effect feuert PageView).
 * - Listener auf `document` (CookieYes dispatcht dort, NICHT auf window – #431).
 * - Returning-User-Polling (200ms, ~10s Cap) bis getCkyConsent verfügbar ist.
 * - Der PageView-/SignupStart-Effect prüft bei JEDEM Lauf den Live-Consent
 *   (Recheck nach Widerruf – window.fbq bleibt sonst bestehen und würde weiter
 *   an Meta senden = DSGVO-Verstoß).
 *
 * No-op, wenn NEXT_PUBLIC_META_PIXEL_ID leer ist (lokal/staging feuert nie).
 */
function useMetaPixel() {
  const pathname = usePathname();
  const [pixelReady, setPixelReady] = useState(false);
  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!pixelId) return;

    const bootstrapPixel = () => {
      if (pixelBootstrapped) {
        // Bereits gebootstrapped (z.B. durch ein zweites Event) → nur State
        // synchronisieren, falls dieser Effect-Lauf das verpasst hat.
        setPixelReady(true);
        return;
      }
      if (!hasAdvertisementConsent()) return;

      pixelBootstrapped = true;

      // Offizielles Meta-Pixel-Bootstrap-Snippet, typsicher nachgebildet (das
      // Original `!function(f,b,e,v,n,t,s){...}` nutzt untypisierte Argumente;
      // wir bauen denselben fbq-Stub-Queue-Mechanismus explizit getypt nach).
      // Eigener Guard `if (window.fbq) return;` entspricht dem `if(f.fbq)return;`
      // des Originals → kein Doppel-Init/Doppel-Script.
      bootstrapFbq();

      // KEIN auto-PageView hier – der route-effect ist Single-Source dafür.
      window.fbq?.('init', pixelId);
      setPixelReady(true);
    };

    // Listener auf `document` (CookieYes-Events dispatchen dort, #431).
    document.addEventListener('cookieyes_banner_load', bootstrapPixel);
    document.addEventListener('cookieyes_consent_update', bootstrapPixel);

    // Returning-User-Polling: bei Wiederkehrern mit gesetztem Consent feuert
    // KEIN consent_update mehr → pollen, bis getCkyConsent verfügbar ist.
    let polls = 0;
    const poll = window.setInterval(() => {
      polls += 1;
      if (window.getCkyConsent?.()) {
        bootstrapPixel();
        window.clearInterval(poll);
      } else if (polls > 50) {
        // ~10s Cap: ohne CookieYes (z.B. Staging) lädt der Pixel nie. OK.
        window.clearInterval(poll);
      } else if (pixelBootstrapped) {
        window.clearInterval(poll);
      }
    }, 200);

    // Sofort versuchen, falls CookieYes beim Mount bereits geladen war.
    bootstrapPixel();

    return () => {
      window.clearInterval(poll);
      document.removeEventListener('cookieyes_banner_load', bootstrapPixel);
      document.removeEventListener('cookieyes_consent_update', bootstrapPixel);
    };
  }, [pixelId]);

  // PageView bei jedem Routenwechsel – mit Live-Consent-Recheck.
  // SignupStart wandert auf den user.signedUp-App-Event (Form-Submit, #561) und
  // wird hier NICHT mehr beim Seitenbesuch von /auth/sign-up gefeuert.
  useEffect(() => {
    if (!pixelReady) return;
    // Live-Recheck (F2): nach Consent-Widerruf bleibt window.fbq bestehen;
    // ohne diesen Check würden weiter PageViews an Meta gehen = DSGVO-Verstoß.
    if (!hasAdvertisementConsent()) return;
    // #592: keine PageViews für interne Test-/Mitarbeiter-Sessions an Meta.
    if (isInternalSession) return;

    window.fbq?.('track', 'PageView');
  }, [pathname, pixelReady]);
}

/**
 * Modul-Flag (Gürtel-und-Hosenträger gegen Race polling↔consent-event): stellt
 * sicher, dass der Google-Tag genau EINMAL gebootstrapped/injiziert wird, selbst
 * wenn Polling und CookieYes-Event quasi-gleichzeitig feuern. Zusätzlich schützt
 * der `window.gtag`-Guard in bootstrapGtag() gegen Doppel-Init.
 */
let gtagBootstrapped = false;

/**
 * Baut den gtag-dataLayer-Stub auf und injiziert gtag.js async – typsichere
 * Nachbildung des offiziellen Google-Tag-Bootstrap-Snippets.
 *
 * Wird NUR nach advertisement-Consent aufgerufen (#717 Änderung 2, Gate liegt in
 * useGoogleAdsTag – exakt das Muster von useMetaPixel). Grund: unsere
 * veröffentlichte Datenschutzerklärung sagt zu Google Ads wörtlich „Bei
 * widerrufener oder nicht erteilter Einwilligung wird der Google-Tag nicht
 * geladen" (Rechtsgrundlage dort ausschliesslich Art. 6 Abs. 1 lit. a DSGVO
 * i.V.m. § 25 TDDDG). Ein unconditional ladender Tag bräche diese Zusage.
 * Entscheidung des Geschäftsführers vom 2026-08-02: Verhalten an den Text
 * angleichen, NICHT den Text ändern. Kosten: kein Consent-Mode-Modeling für
 * Nicht-Einwilligende – bei ~310 Klicks in 3 Wochen rechnerisch null.
 *
 * Consent-Mode-v2-Kommandos bleiben: Default = alles `denied` +
 * `ads_data_redaction:true` + `url_passthrough:true` VOR der ersten
 * Verarbeitung; syncGtagConsent() granted direkt danach die ad_*-Signale (und
 * ein späterer Widerruf setzt sie wieder denied).
 *
 * `analytics_storage` bleibt DAUERHAFT `denied`: wir betreiben kein GA4, es gibt
 * keinen Konsumenten – ein Grant wäre eine tote Berechtigung (Datenminimierung).
 *
 * No-op, wenn NEXT_PUBLIC_GOOGLE_ADS_ID leer ist (lokal/staging feuert nie) –
 * das Prod-Gate bleibt. Enhanced Conversions bewusst NICHT aktiviert.
 */
function bootstrapGtag(): void {
  const adsId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
  if (!adsId) return;
  if (window.gtag) return;

  window.dataLayer = window.dataLayer ?? [];

  // ⛔ Der Stub MUSS das `arguments`-Objekt pushen, NICHT ein Rest-Parameter-
  // Array: gtag.js wertet im dataLayer ausschliesslich `[object Arguments]`-
  // Eintraege als Kommandos aus und ignoriert `[object Array]` stillschweigend
  // (auch beim Replay ab Index 0). Deshalb liegt der Stub als parameterlose
  // `function` in `~/lib/gtag-stub` — vollstaendige Begruendung + #717-Historie
  // stehen dort, Regressionstest in `lib/__tests__/gtag-stub.test.ts`.
  const gtag = createGtagStub(window.dataLayer);

  window.gtag = gtag;

  // Consent Mode v2 ADVANCED – Default: alles denied, BEVOR der Tag verarbeitet.
  // Im denied-Zustand: keine Cookies, cookielose Pings → Conversion-Modeling.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    // Dauerhaft denied: kein GA4 im Einsatz – kein Konsument, kein Grant
    // (Datenminimierung; Ads-Conversion braucht nur ad_storage).
    analytics_storage: 'denied',
  });

  // Datenminimierung im denied-Zustand: IP-/Klick-ID-Redaction an; gclid cookielos
  // über interne Navigationen bis zur Conversion-Page durchreichen.
  gtag('set', 'ads_data_redaction', true);
  gtag('set', 'url_passthrough', true);

  gtag('js', new Date());
  gtag('config', adsId);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${adsId}`;

  // 🔍 Ausnahmen aus gtag.js lesbar machen (#1052). Ohne `crossOrigin` maskiert
  // der Browser jeden Wurf aus einem fremden Skript zu "Script error." — ohne
  // Meldung, ohne Datei, ohne Zeile. Genau in dieser Form liegen die Prod-
  // Vorfaelle vor, und genau deshalb ist ihre Ursache bis heute unbenannt.
  //
  // Sicher, weil gemessen (30.08.2026, `Origin: https://locari.ai`):
  // `googletagmanager.com/gtag/js` antwortet mit
  // `access-control-allow-origin: https://locari.ai`.
  //
  // 🔴 NICHT beim Meta-Pixel machen (`bootstrapMetaPixel` weiter unten):
  // `connect.facebook.net/en_US/fbevents.js` sendet KEINEN
  // `access-control-allow-origin`-Header. Mit diesem Attribut wuerde das Skript
  // von der CORS-Pruefung verworfen und gar nicht mehr ausgefuehrt — aus einem
  // Diagnose-Gewinn wuerde ein Ausfall der Kampagnen-Messung.
  script.crossOrigin = 'anonymous';

  // In den <head>, NICHT vor das erste <script> (= das offizielle Google-Snippet).
  // Dessen `insertBefore(script, firstScript)` landet im <body>, weil dort das
  // erste Script steht – eine DOM-Mutation im Body waehrend React hydriert, und
  // damit React #418 (Hydration-Mismatch: React verwirft das Server-HTML und
  // rendert clientseitig neu).
  //
  // Exakt dieselbe Klasse wie #620 (posthog-Script-Injection, Fix 12.07.:
  // `external_scripts_inject_target: 'head'`). Nach dem PostHog-Fix blieb gtag
  // als zweite Quelle uebrig: 31 Vorkommen bei 31 Nutzern in 7 Tagen, **alle**
  // mit `utm_source=google-ads` + `gclid` – gtag laedt nur bei Ads-Traffic, also
  // trifft es ausschliesslich bezahlte Besucher.
  //
  // `document.head` existiert immer (Parser garantiert es vor dem Body) und liegt
  // ausserhalb des von React hydrierten Baums.
  document.head.appendChild(script);
}

/**
 * Spiegelt den advertisement-Consent-Zustand in gtag (Grant UND Widerruf) –
 * Consent Mode v2 Advanced Update. Wird bei jedem CookieYes-Consent-Event und
 * beim Returning-User-Polling aufgerufen. No-op, solange gtag nicht gebootstrapped
 * ist (window.gtag-Guard).
 */
function syncGtagConsent(): void {
  if (!window.gtag) return;

  const granted = hasAdvertisementConsent();

  window.gtag('consent', 'update', {
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
    // analytics_storage bewusst NICHT updaten → bleibt Default 'denied'.
  });

  // Redaction nur im denied-Zustand aktiv (bei Consent voller Klick-ID-Zugriff).
  window.gtag('set', 'ads_data_redaction', !granted);
}

/**
 * Google-Tag (gtag.js) für Google-Ads-Conversion-Tracking (consent-gated,
 * TTDSG § 25 / Art. 6 Abs. 1 lit. a DSGVO).
 *
 * - Lädt gtag.js NUR nach advertisement-Consent (#717 Änderung 2) – identisches
 *   Muster wie useMetaPixel. Die veröffentlichte Datenschutzerklärung sagt
 *   „Bei widerrufener oder nicht erteilter Einwilligung wird der Google-Tag
 *   nicht geladen"; das Verhalten folgt dem Text (GF-Entscheid 2026-08-02).
 * - Nach dem Bootstrap wird der Consent-Zustand via syncGtagConsent() gespiegelt
 *   (Grant UND Widerruf – window.gtag bleibt nach Widerruf bestehen).
 * - Listener auf `document` (CookieYes dispatcht dort, NICHT auf window – #431).
 * - Returning-User-Polling (200ms, ~10s Cap): bei Wiederkehrern feuert kein
 *   consent_update → pollen, bis getCkyConsent verfügbar ist, dann einmal syncen.
 *
 * No-op, wenn NEXT_PUBLIC_GOOGLE_ADS_ID leer ist (lokal/staging feuert nie) –
 * Prod-Gate bleibt. Die eigentliche Signup-Conversion feuert im
 * `user.signedUp`-Mapping.
 */
function useGoogleAdsTag() {
  const adsId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!adsId) return;

    const bootstrapOrSyncGtag = () => {
      if (!gtagBootstrapped) {
        // Consent-Gate: ohne erteilte Werbe-Einwilligung wird der Tag NICHT
        // geladen (Zusage der Datenschutzerklärung). Ein späteres Grant kommt
        // über den cookieyes_consent_update-Listener hier wieder an.
        if (!hasAdvertisementConsent()) return;

        gtagBootstrapped = true;
        bootstrapGtag();
      }

      // Aktuellen Consent-Zustand spiegeln – auch der Widerruf muss ankommen,
      // solange window.gtag existiert.
      syncGtagConsent();
    };

    // Listener auf `document` (CookieYes-Events dispatchen dort, #431).
    document.addEventListener('cookieyes_banner_load', bootstrapOrSyncGtag);
    document.addEventListener('cookieyes_consent_update', bootstrapOrSyncGtag);

    // Returning-User-Polling: bei Wiederkehrern mit gesetztem Consent feuert
    // KEIN consent_update mehr → pollen, bis getCkyConsent verfügbar ist.
    let polls = 0;
    const poll = window.setInterval(() => {
      polls += 1;
      if (window.getCkyConsent?.()) {
        bootstrapOrSyncGtag();
        window.clearInterval(poll);
      } else if (polls > 50) {
        // ~10s Cap: ohne CookieYes (z.B. Staging) lädt der Tag nie. OK.
        window.clearInterval(poll);
      }
    }, 200);

    // Sofort versuchen, falls CookieYes beim Mount bereits geladen war.
    bootstrapOrSyncGtag();

    return () => {
      window.clearInterval(poll);
      document.removeEventListener(
        'cookieyes_banner_load',
        bootstrapOrSyncGtag,
      );
      document.removeEventListener(
        'cookieyes_consent_update',
        bootstrapOrSyncGtag,
      );
    };
  }, [adsId]);
}

function AnalyticsProviderBrowser(props: React.PropsWithChildren) {
  // CookieYes-Consent-Sync: aktiviert PostHog erst nach User-Einwilligung
  useCookieYesConsentSync();

  // Meta-Pixel „light" (consent-gated) – NACH dem Consent-Sync.
  useMetaPixel();

  // Google-Tag (gtag.js) für Google-Ads-Conversion-Tracking (consent-gated).
  useGoogleAdsTag();

  // Subscribe to app events and map them to analytics actions
  useAnalyticsMapping(analyticsMapping);

  // Report page views to the analytics service
  useReportPageView(useCallback((url) => analytics.trackPageView(url), []));

  // Render children
  return props.children;
}

/**
 * Provider for the analytics service
 */
export function AnalyticsProvider(props: React.PropsWithChildren) {
  if (!isBrowser()) {
    return props.children;
  }

  return <AnalyticsProviderBrowser>{props.children}</AnalyticsProviderBrowser>;
}

/**
 * Hook to report page views to the analytics service
 * @param reportAnalyticsFn
 */
function useReportPageView(reportAnalyticsFn: (url: string) => unknown) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const qs = searchParams.toString();

  const callAnalyticsOnPathChange = useEffectEvent(() => {
    const url = [pathname, qs].filter(Boolean).join('?');

    return reportAnalyticsFn(url);
  });

  useEffect(() => {
    callAnalyticsOnPathChange();
    // #576: bei JEDEM URL-Change (pathname ODER query) – Parität zum bisherigen
    // posthog-js-History-Change-Verhalten, das mit capture_pageview:false entfällt.
  }, [pathname, qs]);
}
